import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../utils/api';
import { Calendar, AlertCircle, Award, Bell } from 'lucide-react';
import PageHeading from '../components/PageHeading';
import DueQueuePanel from '../components/DueQueuePanel';
import PaperworkQueuePanel, {
  PAPERWORK_STAT_CARDS,
  boardOpenRows,
} from '../components/PaperworkQueuePanel';
import { useDueQueueData } from '../hooks/useDueQueueData';

function Dashboard() {
  const [dueFocus, setDueFocus] = useState('');
  const [paperworkFocus, setPaperworkFocus] = useState('');
  const {
    rows: dueQueueRows,
    summary: dueSummary,
    scheduledJobs,
    inProgressJobs,
  } = useDueQueueData();

  const { data: dashboardSummary } = useQuery({
    queryKey: ['jobs', 'dashboard-summary'],
    queryFn: async () => {
      try {
        const response = await api.get('/jobs/dashboard-summary', { skipErrorToast: true });
        return response.data ?? {};
      } catch {
        return {};
      }
    },
    retry: false,
  });

  const { data: paperworkDashboard } = useQuery({
    queryKey: ['paperwork', 'dashboard'],
    queryFn: async () => {
      try {
        const response = await api.get('/paperwork/dashboard', { skipErrorToast: true });
        return response.data?.data ?? {};
      } catch {
        return {};
      }
    },
    retry: false,
  });

  const { data: paperworkBoard } = useQuery({
    queryKey: ['paperwork-admin-board'],
    queryFn: async () => {
      try {
        const response = await api.get('/paperwork/admin/board', { skipErrorToast: true });
        return response.data?.data ?? {};
      } catch {
        return {};
      }
    },
    retry: false,
  });

  const { data: startingSoonData } = useQuery({
    queryKey: ['buildings', 'operation-starting-soon'],
    queryFn: async () => {
      try {
        const response = await api.get('/buildings/operation-starting-soon?days=31', {
          skipErrorToast: true,
        });
        return response.data?.data ?? { items: [] };
      } catch {
        return { items: [] };
      }
    },
    retry: false,
  });
  const startingSoon = Array.isArray(startingSoonData?.items) ? startingSoonData.items : [];

  const toggleDueFocus = (id) => {
    setPaperworkFocus('');
    setDueFocus((prev) => (prev === id ? '' : id));
  };

  const togglePaperworkFocus = (id) => {
    setDueFocus('');
    setPaperworkFocus((prev) => (prev === id ? '' : id));
  };

  const summaryApi = dashboardSummary || {};
  const scheduledCount = Number(summaryApi.scheduled) || 0;
  const inProgressCount = Number(summaryApi.inProgress) || 0;

  const stats = useMemo(
    () => [
      {
        id: 'overdue',
        name: 'Over Due',
        value: dueSummary.overdueSs,
        unit: 'SS (inspections)',
        buildings: dueSummary.overdueBuildings,
        hint: 'Past due ??not the same as Urgent',
        icon: AlertCircle,
        color: 'bg-red-800',
      },
      {
        id: 'urgent',
        name: 'Urgent',
        value: dueSummary.urgentSs,
        unit: 'SS (inspections)',
        buildings: dueSummary.urgentBuildings,
        hint: 'Still due within 0??0 days',
        icon: AlertCircle,
        color: 'bg-red-500',
      },
      {
        id: 'scheduled',
        name: 'Scheduled',
        value: scheduledCount,
        unit: 'inspections',
        icon: Calendar,
        color: 'bg-blue-500',
      },
      {
        id: 'in_progress',
        name: 'In progress',
        value: inProgressCount,
        unit: 'inspections',
        icon: Award,
        color: 'bg-green-500',
      },
    ],
    [dueSummary, scheduledCount, inProgressCount]
  );

  const paperworkSummary = paperworkDashboard?.summary || {};
  const openRows = useMemo(() => boardOpenRows(paperworkBoard), [paperworkBoard]);
  const paperworkStats = useMemo(
    () =>
      PAPERWORK_STAT_CARDS.map((card) => ({
        ...card,
        value:
          card.id === 'confirm'
            ? openRows.filter((row) => row.keys.includes('1')).length
            : card.id === 'repair'
              ? openRows.filter((row) => row.keys.some((key) => key === '2' || key === '3' || key === '5')).length
              : card.id === 'defect_send'
                ? openRows.filter((row) => row.keys.includes('4')).length
                : Number(paperworkSummary.bwof_ready_buildings) || 0,
      })),
    [openRows, paperworkSummary]
  );

  return (
    <div>
      <PageHeading
        title="Dashboard"
        infoTitle="Dashboard"
        infoChildren={
          <p>
            Tap a card to filter the list below — stay here for a quick check. Tap again to show all. Over Due and
            Urgent never overlap (past due vs still due ≤30d). Scheduled / In progress are inspection status
            totals. Paperwork cards are the open desk: Confirm, Repair, Defect send, and buildings ready
            to submit.
          </p>
        }
      />

      {startingSoon.length > 0 ? (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-3">
            <Bell className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-amber-950">
                Contract starting soon ({startingSoon.length})
              </p>
              <p className="text-xs text-amber-800 mt-0.5 mb-2">
                Contract / start date within about one month. That date sets paperwork years only.
              </p>
              <ul className="space-y-1">
                {startingSoon.slice(0, 8).map((b) => (
                  <li key={b.id} className="text-sm text-amber-950">
                    <Link
                      to={`/buildings/${b.id}/edit`}
                      className="font-medium text-amber-900 hover:underline"
                    >
                      {b.name || b.id}
                    </Link>
                    <span className="text-amber-800 tabular-nums">
                      {' '}
                      — starts {String(b.operation_start_date || '').slice(0, 10)}
                      {b.days_until_start != null ? ` (${b.days_until_start}d)` : ''}
                    </span>
                  </li>
                ))}
              </ul>
              {startingSoon.length > 8 ? (
                <p className="text-xs text-amber-700 mt-1">+{startingSoon.length - 8} more</p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((stat) => {
          const Icon = stat.icon;
          const on = dueFocus === stat.id;
          const buildingLine =
            typeof stat.buildings === 'number'
              ? stat.value > 0 || stat.buildings > 0
                ? `across ${stat.buildings} building${stat.buildings === 1 ? '' : 's'}`
                : null
              : null;
          return (
            <button
              key={stat.id}
              type="button"
              onClick={() => toggleDueFocus(stat.id)}
              className={`bg-card rounded-lg shadow p-6 text-left hover:shadow-lg transition-shadow focus:outline-none cursor-pointer border-2 ${
                on ? 'border-primary-500 ring-2 ring-primary-200' : 'border-transparent'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2 tabular-nums">{stat.value}</p>
                  {stat.unit ? (
                    <p className="text-xs font-medium text-gray-600 mt-0.5">{stat.unit}</p>
                  ) : null}
                  {buildingLine ? (
                    <p className="text-xs text-gray-500 mt-1">{buildingLine}</p>
                  ) : null}
                  {!buildingLine && stat.hint ? (
                    <p className="text-xs text-gray-400 mt-1">{stat.hint}</p>
                  ) : null}
                </div>
                <div className={`${stat.color} p-3 rounded-lg shrink-0`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <h3 className="mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wide">Paperwork</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
        {paperworkStats.map((stat) => {
          const Icon = stat.icon;
          const on = paperworkFocus === stat.id;
          return (
            <button
              key={stat.id}
              type="button"
              onClick={() => togglePaperworkFocus(stat.id)}
              className={`bg-card rounded-lg shadow p-6 text-left hover:shadow-lg transition-shadow focus:outline-none cursor-pointer border-2 ${
                on ? 'border-primary-500 ring-2 ring-primary-200' : 'border-transparent'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2 tabular-nums">{stat.value}</p>
                  {stat.unit ? (
                    <p className="text-xs font-medium text-gray-600 mt-0.5">{stat.unit}</p>
                  ) : null}
                  {stat.hint ? <p className="text-xs text-gray-400 mt-1">{stat.hint}</p> : null}
                </div>
                <div className={`${stat.color} p-3 rounded-lg shrink-0`}>
                  <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

      <PaperworkQueuePanel
        data={paperworkDashboard}
        boardRows={openRows}
        focus={paperworkFocus}
        onFocusChange={setPaperworkFocus}
      />

      <DueQueuePanel
        rows={dueQueueRows}
        focus={dueFocus}
        onFocusChange={setDueFocus}
        scheduledJobs={scheduledJobs}
        inProgressJobs={inProgressJobs}
      />
    </div>
  );
}

export default Dashboard;
